import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Building2,
  User,
  Phone,
  MapPin,
  Globe,
  Bed,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Crown,
  Plus,
  Minus,
  Trash2,
  X,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';

const steps = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'property', label: 'Property', icon: Building2 },
  { id: 'rooms', label: 'Rooms', icon: Bed },
  { id: 'plan', label: 'Plan', icon: Crown },
  { id: 'review', label: 'Review', icon: Check },
];

const BED_TYPES = [
  { value: 'Single', label: '1 single bed' },
  { value: 'Twin', label: '2 single beds' },
  { value: 'Double', label: '1 double bed' },
  { value: 'Queen', label: '1 queen bed' },
  { value: 'King', label: '1 king bed' },
];

const makeRoomGroup = (count = 1) => ({
  name: 'Standard Room',
  count,
  base_price: 1200,
  bed_type: 'Twin',
});

const makeFloor = () => ({
  roomCount: 5,
  sameKind: null, // null = not answered yet, true = all same, false = different kinds
  groups: [makeRoomGroup(5)],
});

const QUICK_ROOM_TYPES = [
  { name: 'Standard Room', base_price: 1200, max_occupancy: 2, bed_count: 1, bed_type: 'Twin' },
  { name: 'Deluxe Room', base_price: 2000, max_occupancy: 2, bed_count: 1, bed_type: 'Queen' },
  { name: 'Suite', base_price: 4000, max_occupancy: 4, bed_count: 2, bed_type: 'King' },
  { name: 'Family Room', base_price: 2500, max_occupancy: 4, bed_count: 2, bed_type: 'Double' },
];

// Kept in sync with the pricing on zaffsoftware.com (LandingPage pricingTiers)
const plans = [
  {
    id: 'trial',
    name: 'Free Trial',
    tagline: 'No credit card',
    description: 'Experience the full platform free for 14 days.',
    pricePerRoom: 0,
    badge: null,
    features: [
      'All features unlocked',
      'Up to 10 rooms',
      'Full support during trial',
      'Easy upgrade anytime',
    ],
    excluded: [],
  },
  {
    id: 'basic',
    name: 'Basic',
    tagline: 'Best to start',
    description: 'Everything you need to run the front desk and back office.',
    pricePerRoom: 150,
    badge: null,
    features: [
      'Booking & reservations',
      'Check-in / check-out',
      'Rooms, rates & guest profiles',
      'Housekeeping & maintenance',
      'Employee scheduling & attendance',
      'Daily reports',
      'Ethiopian calendar & Amharic',
      'Cash & bank transfer payments',
    ],
    excluded: ['Accounting & general ledger', 'Payroll & pension', 'VAT / TOT tax reports (ERCA)', 'Integrated payments', 'Reports & revenue analytics', 'Inventory & stock'],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Most complete',
    description: 'The full suite, including accounting, payroll and analytics.',
    pricePerRoom: 200,
    badge: 'Most Popular',
    features: [
      'Everything in Basic',
      'Accounting & general ledger',
      'Employee payroll & pension',
      'VAT / TOT tax reports (ERCA)',
      'Integrated payments',
      'Night audit workflow',
      'Reports & revenue analytics',
      'Inventory & stock',
      'Custom branding & API access',
      'Priority support',
    ],
    excluded: [],
  },
];

const formatPrice = (price) => {
  if (price === 0) { return 'Free'; }
  return `${Number(price).toLocaleString('en-ET')} ETB`;
};

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_password: '',
    admin_confirm_password: '',
    hotel_name: '',
    subdomain: '',
    phone: '',
    address: '',
    city: 'Addis Ababa',
    country: 'Ethiopia',
    plan_id: 'trial',
    floors: [makeFloor()],
  });
  const [activeFloor, setActiveFloor] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Auto-generate subdomain whenever hotel name changes
  useEffect(() => {
    const generated = generateSubdomain(formData.hotel_name);
    setFormData((prev) => ({ ...prev, subdomain: generated }));
  }, [formData.hotel_name]);

  const generateSubdomain = (hotelName) => {
    if (!hotelName || hotelName.trim().length < 2) { return ''; }

    const slug = hotelName
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);

    return slug.length >= 3 ? slug : '';
  };

  const checkSubdomainAvailability = async (subdomain) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const response = await fetch(`${apiUrl}/tenants/check-subdomain/${subdomain}`);
    const data = await response.json();
    return data.success && data.data?.available === true;
  };

  const ensureUniqueSubdomain = async (hotelName) => {
    let base = generateSubdomain(hotelName);

    // Fallback for very short hotel names
    if (!base || base.length < 3) {
      base = `hotel-${Math.floor(1000 + Math.random() * 9000)}`;
    }

    let candidate = base;
    let attempts = 0;

    while (attempts < 50) {
      try {
        const available = await checkSubdomainAvailability(candidate);
        if (available) { return candidate; }
      } catch (error) {
        console.error('Subdomain check failed:', error);
        // Return candidate and let backend validation handle it
        return candidate;
      }
      attempts++;
      candidate = `${base}-${attempts}`;
    }

    // Last resort: append timestamp suffix
    return `${base}-${Date.now().toString().slice(-4)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'subdomain' ? normalizeSubdomain(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const normalizeSubdomain = (value) => {
    if (!value) { return value; }
    return value
      .toLowerCase()
      .trim()
      .split('.')[0]
      .replace(/[^a-z0-9-]/g, '')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };

  const updateFloor = (floorIdx, updater) => {
    setFormData((prev) => ({
      ...prev,
      floors: prev.floors.map((f, i) => (i === floorIdx ? updater(f) : f)),
    }));
  };

  const setFloorRoomCount = (floorIdx, value) => {
    const roomCount = Math.min(200, Math.max(1, value));
    updateFloor(floorIdx, (f) => ({
      ...f,
      roomCount,
      // Keep a single "all the same" group in sync with the room count.
      groups: f.sameKind === false
        ? f.groups
        : [{ ...(f.groups[0] || makeRoomGroup()), count: roomCount }],
    }));
  };

  const setSameKind = (floorIdx, same) => {
    updateFloor(floorIdx, (f) => ({
      ...f,
      sameKind: same,
      groups: same
        ? [{ ...(f.groups[0] || makeRoomGroup()), count: f.roomCount }]
        : (f.groups.length > 0 ? f.groups : [makeRoomGroup(f.roomCount)]),
    }));
  };

  const updateGroup = (floorIdx, groupIdx, field, value) => {
    updateFloor(floorIdx, (f) => ({
      ...f,
      groups: f.groups.map((g, i) => (i === groupIdx ? { ...g, [field]: value } : g)),
    }));
  };

  const setGroupCount = (floorIdx, groupIdx, delta) => {
    updateFloor(floorIdx, (f) => ({
      ...f,
      groups: f.groups.map((g, i) =>
        i === groupIdx ? { ...g, count: Math.max(1, (parseInt(g.count) || 1) + delta) } : g,
      ),
    }));
  };

  const addGroup = (floorIdx, template) => {
    updateFloor(floorIdx, (f) => ({
      ...f,
      groups: [...f.groups, { ...makeRoomGroup(1), ...(template || {}) }],
    }));
  };

  const removeGroup = (floorIdx, groupIdx) => {
    updateFloor(floorIdx, (f) => ({
      ...f,
      groups: f.groups.filter((_, i) => i !== groupIdx),
    }));
  };

  const addFloor = () => {
    setFormData((prev) => ({ ...prev, floors: [...prev.floors, makeFloor()] }));
    setActiveFloor(formData.floors.length);
  };

  const removeFloor = (floorIdx) => {
    setFormData((prev) => ({
      ...prev,
      floors: prev.floors.filter((_, i) => i !== floorIdx),
    }));
    setActiveFloor((prev) => Math.max(0, Math.min(prev, formData.floors.length - 2)));
  };

  const copyFloor = (floorIdx) => {
    updateFloor(floorIdx, () => {
      const src = formData.floors[floorIdx - 1];
      return {
        roomCount: src.roomCount,
        sameKind: src.sameKind,
        groups: src.groups.map((g) => ({ ...g })),
      };
    });
  };

  const floorAssignedRooms = (floor) =>
    floor.groups.reduce((sum, g) => sum + (parseInt(g.count) || 0), 0);

  const totalRooms = formData.floors.reduce((sum, f) => sum + (parseInt(f.roomCount) || 0), 0);

  const validateStep = () => {
    const errors = {};
    const step = steps[currentStep].id;

    if (step === 'account') {
      if (!formData.admin_first_name.trim()) { errors.admin_first_name = 'First name is required'; }
      if (!formData.admin_last_name.trim()) { errors.admin_last_name = 'Last name is required'; }
      if (!formData.admin_email.trim()) { errors.admin_email = 'Email is required'; } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) { errors.admin_email = 'Valid email required'; }
      if (!formData.admin_password) { errors.admin_password = 'Password is required'; } else if (formData.admin_password.length < 8) { errors.admin_password = 'Password must be at least 8 characters'; } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.admin_password)) {
        errors.admin_password = 'Password must include uppercase, lowercase, and number';
      }
      if (formData.admin_password !== formData.admin_confirm_password) {
        errors.admin_confirm_password = 'Passwords do not match';
      }
    }

    if (step === 'property') {
      if (!formData.hotel_name.trim()) { errors.hotel_name = 'Hotel name is required'; } else if (formData.hotel_name.trim().length < 2) { errors.hotel_name = 'Hotel name must be at least 2 characters'; }
      if (!formData.city.trim()) { errors.city = 'City is required'; }
      if (!formData.country.trim()) { errors.country = 'Country is required'; }
    }

    if (step === 'rooms') {
      formData.floors.forEach((floor, fi) => {
        const label = `Floor ${fi + 1}`;
        if ((parseInt(floor.roomCount) || 0) < 1) {
          errors[`floors[${fi}].roomCount`] = `${label}: add at least 1 room`;
        }
        if (floor.sameKind === null) {
          errors[`floors[${fi}].sameKind`] = `${label}: tell us if the rooms are all the same kind`;
          return;
        }
        const assigned = floorAssignedRooms(floor);
        if (floor.groups.length === 0) {
          errors[`floors[${fi}].groups`] = `${label}: set up at least one room`;
        } else if (assigned !== floor.roomCount) {
          errors[`floors[${fi}].groups`] = `${label}: ${assigned} of ${floor.roomCount} rooms set up. They must add up`;
        }
        floor.groups.forEach((g, gi) => {
          if (!String(g.name || '').trim()) {
            errors[`floors[${fi}].groups[${gi}].name`] = `${label}: room kind ${gi + 1} needs a name`;
          }
        });
      });
      if (Object.keys(errors).length > 0) {
        // Jump to the first floor that has a problem.
        const firstBad = formData.floors.findIndex((_, fi) =>
          Object.keys(errors).some((k) => k.startsWith(`floors[${fi}]`)));
        if (firstBad >= 0) { setActiveFloor(firstBad); }
      }
    }

    if (step === 'plan') {
      if (!formData.plan_id) { errors.plan_id = 'Select a plan'; }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    setError('');
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) { return; }

    setLoading(true);
    setError('');

    // Generate a unique subdomain from the hotel name
    const uniqueSubdomain = await ensureUniqueSubdomain(formData.hotel_name);

    const payload = {
      hotel_name: formData.hotel_name,
      subdomain: uniqueSubdomain,
      admin_email: formData.admin_email,
      admin_password: formData.admin_password,
      admin_first_name: formData.admin_first_name,
      admin_last_name: formData.admin_last_name,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      country: formData.country,
      plan_id: formData.plan_id,
      num_floors: formData.floors.length,
      room_config: {
        num_floors: formData.floors.length,
        groups: formData.floors.flatMap((floor, fi) =>
          floor.groups.map((g) => ({
            floor: fi + 1,
            type_name: g.name,
            base_price: g.base_price,
            bed_type: g.bed_type,
            count: g.count,
          }))),
      },
    };

    const result = await register(payload);

    if (result.success) {
      setSuccess(true);
      const isPaidPlan = ['basic', 'pro'].includes(formData.plan_id);
      if (isPaidPlan) {
        // Store tenant info for payment page
        sessionStorage.setItem('pendingPayment', JSON.stringify({
          tenantId: result.data?.tenant?.id,
          tenantName: formData.hotel_name,
          planId: formData.plan_id,
          planName: plans.find(p => p.id === formData.plan_id)?.name,
          pricePerRoom: plans.find(p => p.id === formData.plan_id)?.pricePerRoom,
          roomCount: totalRooms,
          adminEmail: formData.admin_email,
        }));
        setTimeout(() => navigate('/payment', { replace: true }), 800);
      } else {
        setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
      }
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }

    setLoading(false);
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : isCompleted
                      ? 'bg-ethiopian-green text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span
                className={`text-xs font-medium ${
                  isActive ? 'text-primary-600' : isCompleted ? 'text-ethiopian-green' : 'text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-600 transition-all duration-300"
          style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderAccountStep = () => (
    <div className="space-y-5 animate-fade-in">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <User className="w-5 h-5 text-primary-600" />
        {t('auth.register.adminInfo', 'Create your administrator account')}
      </h3>
      <p className="text-sm text-gray-600">Staff accounts are created later by the Hotel Admin.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
          <input
            type="text"
            name="admin_first_name"
            value={formData.admin_first_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Abebe"
          />
          {fieldErrors.admin_first_name && <p className="text-red-600 text-xs mt-1">{fieldErrors.admin_first_name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
          <input
            type="text"
            name="admin_last_name"
            value={formData.admin_last_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Kebede"
          />
          {fieldErrors.admin_last_name && <p className="text-red-600 text-xs mt-1">{fieldErrors.admin_last_name}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <div className="relative">
          <input
            type="email"
            name="admin_email"
            value={formData.admin_email}
            onChange={handleChange}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="admin@grandhotel.com"
          />
          <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
        {fieldErrors.admin_email && <p className="text-red-600 text-xs mt-1">{fieldErrors.admin_email}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="admin_password"
              value={formData.admin_password}
              onChange={handleChange}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="••••••••"
            />
            <Lock className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {fieldErrors.admin_password && <p className="text-red-600 text-xs mt-1">{fieldErrors.admin_password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="admin_confirm_password"
              value={formData.admin_confirm_password}
              onChange={handleChange}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="••••••••"
            />
            <Lock className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {fieldErrors.admin_confirm_password && (
            <p className="text-red-600 text-xs mt-1">{fieldErrors.admin_confirm_password}</p>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500">Password must be at least 8 characters with uppercase, lowercase, and a number.</p>
    </div>
  );

  const renderPropertyStep = () => (
    <div className="space-y-5 animate-fade-in">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Building2 className="w-5 h-5 text-primary-600" />
        {t('auth.register.hotelInfo', 'Tell us about your property')}
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
        <input
          type="text"
          name="hotel_name"
          value={formData.hotel_name}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Grand Hotel Ethiopia"
        />
        {fieldErrors.hotel_name && <p className="text-red-600 text-xs mt-1">{fieldErrors.hotel_name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <div className="relative">
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="+251 91 234 5678"
          />
          <Phone className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <div className="relative">
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Bole Road, Addis Ababa"
          />
          <MapPin className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {fieldErrors.city && <p className="text-red-600 text-xs mt-1">{fieldErrors.city}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
          <div className="relative">
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <Globe className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          </div>
          {fieldErrors.country && <p className="text-red-600 text-xs mt-1">{fieldErrors.country}</p>}
        </div>
      </div>
    </div>
  );

  const renderGroupEditor = (floorIdx, groupIdx, group, showCount) => {
    const floor = formData.floors[floorIdx];
    return (
      <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
          <input
            type="text"
            value={group.name}
            onChange={(e) => updateGroup(floorIdx, groupIdx, 'name', e.target.value)}
            placeholder="Room kind name (e.g. Standard)"
            className="text-base font-semibold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary-400 focus:outline-none px-1 py-0.5 w-56"
          />
          {showCount && floor.groups.length > 1 && (
            <button
              type="button"
              onClick={() => removeGroup(floorIdx, groupIdx)}
              className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50"
              title="Remove this room kind"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* Quick-pick room kind chips */}
          <div className="flex flex-wrap gap-2">
            {QUICK_ROOM_TYPES.map((qt) => (
              <button
                key={qt.name}
                type="button"
                onClick={() => {
                  updateGroup(floorIdx, groupIdx, 'name', qt.name);
                  updateGroup(floorIdx, groupIdx, 'base_price', qt.base_price);
                  updateGroup(floorIdx, groupIdx, 'bed_type', qt.bed_type);
                }}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  group.name === qt.name
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100'
                }`}
              >
                {qt.name}
              </button>
            ))}
          </div>

          {/* How many rooms of this kind (only when splitting a floor) */}
          {showCount && (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-700">How many rooms?</div>
                <div className="text-xs text-gray-400">Rooms of this kind on Floor {floorIdx + 1}</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setGroupCount(floorIdx, groupIdx, -1)}
                  disabled={(parseInt(group.count) || 1) <= 1}
                  className="w-9 h-9 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center text-2xl font-bold text-gray-900 tabular-nums">
                  {parseInt(group.count) || 1}
                </span>
                <button
                  type="button"
                  onClick={() => setGroupCount(floorIdx, groupIdx, 1)}
                  className="w-9 h-9 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Price per night */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700">Price per night</div>
              <div className="text-xs text-gray-400">In Ethiopian Birr (ETB)</div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500 font-medium">ETB</span>
              <input
                type="number"
                min="0"
                value={group.base_price}
                onChange={(e) => updateGroup(floorIdx, groupIdx, 'base_price', parseFloat(e.target.value) || 0)}
                className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Bed picker */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Beds in the room</div>
            <div className="flex flex-wrap gap-2">
              {BED_TYPES.map((bt) => (
                <button
                  key={bt.value}
                  type="button"
                  onClick={() => updateGroup(floorIdx, groupIdx, 'bed_type', bt.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all font-medium ${
                    group.bed_type === bt.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {bt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRoomsStep = () => {
    const floor = formData.floors[activeFloor];
    const floorNum = activeFloor + 1;
    const assigned = floorAssignedRooms(floor);
    const roomPreview = floor.roomCount >= 1
      ? `${floorNum}01-${floorNum}${String(floor.roomCount).padStart(2, '0')}`
      : '';

    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Bed className="w-5 h-5 text-primary-600" />
            Tell us about your rooms
          </h3>
          <p className="text-sm text-gray-500 mt-1">Set up one floor at a time. We’ll handle the room numbers.</p>
        </div>

        {/* Floor tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {formData.floors.map((f, i) => {
            const isDone = f.sameKind !== null && floorAssignedRooms(f) === f.roomCount;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveFloor(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all flex items-center gap-1.5 ${
                  i === activeFloor
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {isDone && <Check className="w-3.5 h-3.5 text-ethiopian-green" />}
                Floor {i + 1}
              </button>
            );
          })}
          <button
            type="button"
            onClick={addFloor}
            disabled={formData.floors.length >= 30}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-40"
          >
            <Plus className="w-4 h-4" />
            Add floor
          </button>
        </div>

        {/* Active floor card */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="font-semibold text-gray-900">Floor {floorNum}</div>
            <div className="flex items-center gap-3">
              {activeFloor > 0 && (
                <button
                  type="button"
                  onClick={() => copyFloor(activeFloor)}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  Same as Floor {activeFloor}? Copy it
                </button>
              )}
              {formData.floors.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeFloor(activeFloor)}
                  className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                  title="Remove this floor"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove floor
                </button>
              )}
            </div>
          </div>

          <div className="p-4 space-y-5">
            {/* How many rooms on this floor */}
            <div>
              <p className="text-sm font-medium text-gray-800 mb-3">
                How many rooms are on Floor {floorNum}?
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setFloorRoomCount(activeFloor, floor.roomCount - 1)}
                  disabled={floor.roomCount <= 1}
                  className="w-10 h-10 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={floor.roomCount}
                  onChange={(e) => setFloorRoomCount(activeFloor, parseInt(e.target.value) || 1)}
                  className="w-16 text-center text-2xl font-bold text-gray-900 border border-gray-300 rounded-lg py-1 focus:ring-2 focus:ring-primary-400 focus:border-transparent tabular-nums"
                />
                <button
                  type="button"
                  onClick={() => setFloorRoomCount(activeFloor, floor.roomCount + 1)}
                  className="w-10 h-10 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <div className="flex gap-2 ml-2 flex-wrap">
                  {[5, 10, 15, 20].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setFloorRoomCount(activeFloor, n)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        floor.roomCount === n
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                These will become rooms {roomPreview}.
              </p>
              {fieldErrors[`floors[${activeFloor}].roomCount`] && (
                <p className="text-red-600 text-xs mt-1">{fieldErrors[`floors[${activeFloor}].roomCount`]}</p>
              )}
            </div>

            {/* Same or different? */}
            <div>
              <p className="text-sm font-medium text-gray-800 mb-3">
                Are the rooms on this floor all the same kind?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSameKind(activeFloor, true)}
                  className={`px-4 py-3 rounded-xl border-2 text-left transition-all ${
                    floor.sameKind === true
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                    {floor.sameKind === true && <Check className="w-4 h-4 text-primary-600" />}
                    Yes, all the same
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Set the price and beds once for all {floor.roomCount} rooms</div>
                </button>
                <button
                  type="button"
                  onClick={() => setSameKind(activeFloor, false)}
                  className={`px-4 py-3 rounded-xl border-2 text-left transition-all ${
                    floor.sameKind === false
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                    {floor.sameKind === false && <Check className="w-4 h-4 text-primary-600" />}
                    No, some are different
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Split the {floor.roomCount} rooms into different kinds</div>
                </button>
              </div>
              {fieldErrors[`floors[${activeFloor}].sameKind`] && (
                <p className="text-red-600 text-xs mt-2">{fieldErrors[`floors[${activeFloor}].sameKind`]}</p>
              )}
            </div>

            {/* Room kind editor(s) */}
            {floor.sameKind === true && (
              <div>
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Tell us about these rooms
                </p>
                {floor.groups[0] && renderGroupEditor(activeFloor, 0, floor.groups[0], false)}
              </div>
            )}

            {floor.sameKind === false && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-800">
                    Set up your {floor.roomCount} rooms
                  </p>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    assigned === floor.roomCount
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {assigned} of {floor.roomCount} rooms set up
                  </span>
                </div>
                {fieldErrors[`floors[${activeFloor}].groups`] && (
                  <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                    {fieldErrors[`floors[${activeFloor}].groups`]}
                  </p>
                )}
                {floor.groups.map((g, gi) => (
                  <div key={gi}>
                    {renderGroupEditor(activeFloor, gi, g, true)}
                    {fieldErrors[`floors[${activeFloor}].groups[${gi}].name`] && (
                      <p className="text-red-600 text-xs mt-1">
                        {fieldErrors[`floors[${activeFloor}].groups[${gi}].name`]}
                      </p>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addGroup(activeFloor)}
                  className="w-full py-3 border-2 border-dashed border-yellow-400 rounded-xl text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 hover:border-yellow-500 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add a different kind of room
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Add another floor */}
        <button
          type="button"
          onClick={addFloor}
          disabled={formData.floors.length >= 30}
          className="w-full py-3 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
          Add another floor
        </button>

        {/* Summary bar */}
        <div className="bg-gradient-to-r from-primary-50 to-green-50 border border-primary-100 rounded-xl p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {formData.floors.length} {formData.floors.length === 1 ? 'floor' : 'floors'}
            </div>
            <div className="text-xs text-gray-500">Room numbers are assigned automatically per floor</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-700">{totalRooms}</div>
            <div className="text-xs text-gray-500">total rooms</div>
          </div>
        </div>
      </div>
    );
  };

  const renderPlanStep = () => (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Crown className="w-5 h-5 text-primary-600" />
          Choose your plan
        </h3>
        <p className="text-sm text-gray-500 mt-1">All plans are billed <span className="font-medium text-gray-700">per room, per month</span>. No room limits. Cancel anytime.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const isSelected = formData.plan_id === plan.id;
          const isPremium = plan.id === 'pro';
          return (
            <label
              key={plan.id}
              className={`relative flex flex-col rounded-2xl cursor-pointer transition-all duration-200 overflow-hidden
                ${isSelected
              ? isPremium
                ? 'border-2 border-primary-600 shadow-lg shadow-primary-100'
                : 'border-2 border-ethiopian-green shadow-md shadow-green-100'
              : 'border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
            }
                ${isPremium ? 'bg-gradient-to-b from-primary-900 to-primary-800 text-white' : 'bg-white'}
              `}
            >
              <input
                type="radio"
                name="plan_id"
                value={plan.id}
                checked={isSelected}
                onChange={handleChange}
                className="sr-only"
              />

              {/* Badge */}
              {plan.badge && (
                <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                  {plan.badge}
                </div>
              )}

              {/* Header */}
              <div className={`px-5 pt-5 pb-4 border-b ${isPremium ? 'border-primary-700' : 'border-gray-100'}`}>
                <div className={`text-xs font-semibold uppercase tracking-widest mb-1 ${isPremium ? 'text-primary-300' : 'text-primary-600'}`}>
                  {plan.tagline}
                </div>
                <div className={`text-xl font-bold mb-1 ${isPremium ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </div>
                <div className={`text-xs mb-4 ${isPremium ? 'text-primary-200' : 'text-gray-500'}`}>
                  {plan.description}
                </div>
                {/* Price */}
                {plan.pricePerRoom === 0
                  ? (
                    <div>
                      <span className="text-3xl font-extrabold text-ethiopian-green">Free</span>
                      <span className={`text-xs ml-1 ${isPremium ? 'text-primary-300' : 'text-gray-400'}`}>for 14 days</span>
                    </div>
                  )
                  : (
                    <div className="flex items-end gap-1">
                      <span className={`text-3xl font-extrabold ${isPremium ? 'text-white' : 'text-primary-700'}`}>
                        {formatPrice(plan.pricePerRoom)}
                      </span>
                      <span className={`text-xs pb-1 ${isPremium ? 'text-primary-300' : 'text-gray-400'}`}>/room/month</span>
                    </div>
                  )}
              </div>

              {/* Features */}
              <div className="px-5 py-4 flex-1 space-y-1.5">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isPremium ? 'text-green-400' : 'text-ethiopian-green'}`} />
                    <span className={`text-xs leading-snug ${isPremium ? 'text-primary-100' : 'text-gray-700'}`}>{feature}</span>
                  </div>
                ))}

                {/* Excluded features */}
                {plan.excluded.length > 0 && (
                  <div className={`pt-2 mt-2 border-t ${isPremium ? 'border-primary-700' : 'border-gray-100'}`}>
                    <div className={`text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${isPremium ? 'text-primary-400' : 'text-gray-400'}`}>
                      Not included
                    </div>
                    {plan.excluded.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1">
                        <X className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isPremium ? 'text-primary-500' : 'text-gray-300'}`} />
                        <span className={`text-xs leading-snug ${isPremium ? 'text-primary-400' : 'text-gray-400'}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Select indicator */}
              <div className={`mx-5 mb-5 mt-2 py-2 rounded-lg text-center text-xs font-semibold transition-colors
                ${isSelected
              ? isPremium
                ? 'bg-white text-primary-800'
                : 'bg-ethiopian-green text-white'
              : isPremium
                ? 'bg-primary-700 text-primary-200 hover:bg-primary-600'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}>
                {isSelected ? '✓ Selected' : 'Select plan'}
              </div>
            </label>
          );
        })}
      </div>
      {fieldErrors.plan_id && <p className="text-red-600 text-sm">{fieldErrors.plan_id}</p>}
    </div>
  );

  const renderReviewStep = () => {
    const selectedPlan = plans.find((p) => p.id === formData.plan_id);
    return (
      <div className="space-y-5 animate-fade-in">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Check className="w-5 h-5 text-primary-600" />
          Review and go live
        </h3>

        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center pb-3 border-b border-gray-200">
            <div>
              <div className="text-sm font-medium text-gray-900">{formData.hotel_name}</div>
              <div className="text-xs text-gray-500">Subdomain auto-generated from hotel name</div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="text-xs text-primary-600 hover:underline"
            >
              Edit
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-500 text-xs">Admin</div>
              <div className="font-medium text-gray-900">
                {formData.admin_first_name} {formData.admin_last_name}
              </div>
              <div className="text-gray-600 text-xs">{formData.admin_email}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Location</div>
              <div className="font-medium text-gray-900">
                {formData.city}, {formData.country}
              </div>
              <div className="text-gray-600 text-xs">{formData.address || 'No address provided'}</div>
            </div>
          </div>

          <div className="pb-3 border-b border-gray-200">
            <div className="text-gray-500 text-xs mb-1">Room Configuration</div>
            <div className="space-y-2">
              {formData.floors.map((floor, fi) => (
                <div key={fi} className="text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-800">Floor {fi + 1}</span>
                    <span className="text-gray-500 text-xs mt-0.5">
                      rooms {fi + 1}01-{fi + 1}{String(floor.roomCount).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="ml-3 space-y-0.5 mt-0.5">
                    {floor.groups.map((g, gi) => (
                      <div key={gi} className="flex justify-between text-xs text-gray-600">
                        <span>{g.name} · {BED_TYPES.find(b => b.value === g.bed_type)?.label || g.bed_type}</span>
                        <span className="font-medium text-gray-800">
                          {g.count} × {formatPrice(g.base_price)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-sm font-medium text-primary-700">
              Total: {totalRooms} rooms on {formData.floors.length} {formData.floors.length === 1 ? 'floor' : 'floors'}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-gray-500 text-xs">Selected Plan</div>
                <div className="font-semibold text-gray-900">{selectedPlan?.name}</div>
                <div className="text-xs text-gray-600">
                  {selectedPlan?.pricePerRoom === 0 ? 'Free Trial (14 days)' : `${formatPrice(selectedPlan?.pricePerRoom || 0)}/room/month`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="text-xs text-primary-600 hover:underline"
              >
                Edit
              </button>
            </div>

            {/* Payment breakdown for paid plans */}
            {selectedPlan && selectedPlan.pricePerRoom > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-3">Monthly Payment Estimate</div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{totalRooms} rooms × {formatPrice(selectedPlan.pricePerRoom)}</span>
                  <span className="font-medium text-gray-900">{formatPrice(totalRooms * selectedPlan.pricePerRoom)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">15% VAT</span>
                  <span className="font-medium text-gray-900">{formatPrice(Math.round(totalRooms * selectedPlan.pricePerRoom * 0.15))}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t border-amber-300 pt-2 mt-1">
                  <span className="text-gray-900">Total / month</span>
                  <span className="text-primary-700">{formatPrice(Math.round(totalRooms * selectedPlan.pricePerRoom * 1.15))}</span>
                </div>
                <p className="text-xs text-amber-700 mt-2">Payment instructions will be shown on the next screen.</p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || success}
          className="w-full bg-ethiopian-green text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {loading
            ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating your hotel...
              </>
            )
            : success
              ? (
                <>
                  <Check className="w-5 h-5" />
              Created! Redirecting to dashboard...
                </>
              )
              : (
                <>
                  <Sparkles className="w-5 h-5" />
              Create Hotel & Go Live
                </>
              )}
        </button>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'account':
        return renderAccountStep();
      case 'property':
        return renderPropertyStep();
      case 'rooms':
        return renderRoomsStep();
      case 'plan':
        return renderPlanStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-primary-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('auth.register.title', 'Set Up Your Hotel')}
            </h1>
            <p className="text-gray-600">
              {t('auth.register.subtitle', 'Complete these steps to start managing your property')}
            </p>
          </div>

          {renderStepIndicator()}

          <form onSubmit={handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
              {steps[currentStep].id !== 'review' && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center border-t pt-6">
            <p className="text-sm text-gray-600">
              {t('auth.register.haveAccount', 'Already have an account?')}{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                {t('auth.register.signIn', 'Sign in')}
              </Link>
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Staff accounts are invite-only and created by the Hotel Admin after setup.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
